import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'

import { Dialog, Button, Form, Text, Alert } from '@/ui'
import {
  ADMIN_CREATE_USER_TAX_ESTIMATE_KEY,
  adminCreateUserTaxEstimate,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { STATES, isValidUSState } from '@/constants/locationConstants'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

/**
 * STATES includes non-state options (military codes, 'Unsure') that the
 * backend's USStates enum validation rejects — only offer real states.
 */
const FILING_STATE_OPTIONS = STATES.filter(({ value }) => isValidUSState(value))

interface AddStateEstimateFormValues {
  taxQuarter: string
  filingState: string
  estimateInCents: number | null
  amountPaidInCents: number | null
  paidAt: string
}

interface AddStateEstimateDialogProps {
  open: boolean
  close: () => void
  userId: number
  /** Quarters the user already has estimates for, in YYYY-Q format. */
  taxQuarters: string[]
}

/**
 * Creates a tax-estimate record for an additional filing state (multi-state
 * users don't get secondary-state records auto-generated). Payment fields
 * are optional so ops can create the record and log an already-made payment
 * in one step; later edits go through the existing Update dialog.
 */
export const AddStateEstimateDialog = ({
  open,
  close,
  userId,
  taxQuarters,
}: AddStateEstimateDialogProps) => {
  const dispatch = useAppDispatch()
  const error = useReselector(getFetchError, ADMIN_CREATE_USER_TAX_ESTIMATE_KEY)

  const quarterOptions = useMemo(
    () =>
      taxQuarters.map((taxQuarter) => {
        const [taxYear, taxQuarterNum] = taxQuarter.split('-')
        return { text: `Q${taxQuarterNum}, ${taxYear}`, value: taxQuarter }
      }),
    [taxQuarters]
  )

  const validationSchema = yup.object({
    taxQuarter: yup.string().required('Select a quarter.'),
    filingState: yup.string().required('Select a filing state.'),
    estimateInCents: yup.number().nullable(),
    amountPaidInCents: yup.number().nullable(),
    paidAt: yup
      .string()
      .nullable()
      .when('amountPaidInCents', ([amountPaidInCents], schema) =>
        amountPaidInCents > 0
          ? schema.required('Enter the date the payment was made.')
          : schema
      ),
  })

  const formik = useFormik<AddStateEstimateFormValues>({
    initialValues: {
      taxQuarter: taxQuarters[0] ?? '',
      filingState: '',
      estimateInCents: null,
      amountPaidInCents: null,
      paidAt: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const res = await adminCreateUserTaxEstimate(userId, {
        taxQuarter: values.taxQuarter,
        filingState: values.filingState,
        estimateInCents: values.estimateInCents,
        amountPaidInCents: values.amountPaidInCents,
        paidAt: values.paidAt ? new Date(values.paidAt).getTime() : null,
        quarterPaid: values.paidAt ? true : null,
      })(dispatch)
      if (res) {
        close()
      }
    },
  })

  return (
    <Dialog open={open} onClose={close}>
      <Dialog.Content>
        <Text variant={TypographyStyles.H1}>Add state estimate record</Text>
        <Text variant={TypographyStyles.BODY_MD} className="my-4">
          Adds a quarterly estimate record for an additional filing state so
          payments can be logged against it and credited in the next
          calculation.
        </Text>
        {error && <Alert variant="error">{error.message}</Alert>}
        <FormikProvider value={formik}>
          <div className="space-y-4">
            <Text variant={TypographyStyles.EYEBROW}>Tax Quarter</Text>
            <Form.Select name="taxQuarter" options={quarterOptions} />

            <Text variant={TypographyStyles.EYEBROW}>Filing State</Text>
            <Form.Select name="filingState" options={FILING_STATE_OPTIONS} />

            <Text variant={TypographyStyles.EYEBROW}>
              Quarterly Tax Estimate (optional)
            </Text>
            <Form.Input
              name="estimateInCents"
              componentType="currencyInCents"
              prefix="$"
            />

            <Text variant={TypographyStyles.EYEBROW}>
              Amount Already Paid (optional)
            </Text>
            <Form.Input
              name="amountPaidInCents"
              componentType="currencyInCents"
              prefix="$"
            />

            <Text variant={TypographyStyles.EYEBROW}>
              Paid At (required when logging a payment)
            </Text>
            <Form.DateInput name="paidAt" maxDate={new Date()} />
          </div>
          <Dialog.Footer className="mt-4">
            <Dialog.Close asChild>
              <Button variant="secondary" onClick={close}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              loading={formik.isSubmitting}
              onClick={() => formik.handleSubmit()}
            >
              Create
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
